<script setup lang="ts">
import { IconX } from '@tabler/icons-vue';
import { DialogRoot, DialogPortal, DialogOverlay, DialogContent, DialogTitle, DialogClose } from 'radix-vue';
import { ref, computed } from 'vue';

import Button from './Button.vue';
import ThemeProvider from './ThemeProvider.vue';

type StudioButtonVariant = 'filled' | 'outline' | 'text' | 'warn';
export type DialogSize = 'medium' | 'large' | 'xlarge' | '2xlarge' | 'h-fit' | 'small';

const props = defineProps({
  title: { type: String },
  primaryActionTitle: { type: String, default: '' },
  primaryActionVariant: {
    type: String as () => StudioButtonVariant,
    default: 'filled',
    validator: (value: string) => ['filled', 'outline', 'text', 'warn'].includes(value)
  },
  primaryActionDisabled: { type: Boolean, default: false },
  secondaryActionTitle: { type: String, default: '' },
  secondaryActionDisabled: { type: Boolean, default: false },
  actionsDisabled: { type: Boolean, default: false },
  preventClose: { type: Boolean, default: false },
  preventCloseMessage: {
    type: String,
    default: 'Are you sure you want to close this dialog?'
  },
  size: {
    type: String as () => DialogSize,
    default: 'medium',
    validator: (prop: string) => ['small', 'medium', 'large', 'xlarge', '2xlarge', 'h-fit'].includes(prop)
  },
  showCloseButton: {
    type: Boolean,
    default: true
  },
  showOverlay: {
    type: Boolean,
    default: true
  },
  message: {
    type: String,
    default: ''
  },
  theme: {
    type: String as () => ThemeMode,
    default: 'fixed',
    validator: (value: string) => ['user-configured', 'fixed', 'inherit'].includes(value)
  }
});

type ThemeMode = 'user-configured' | 'fixed' | 'inherit';

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'primaryAction'): void;
  (e: 'secondaryAction'): void;
  (e: 'onBlur', ev: Event): void;
}>();

const show = ref(true);

const showFooter = computed(() => {
  return !!(props.primaryActionTitle || props.secondaryActionTitle);
});

const handleClose = () => {
  if (props.preventClose && !window.confirm(props.preventCloseMessage)) {
    return;
  }
  show.value = false;
  emit('close');
};

const handleOpenChange = (isOpen: boolean) => {
  if (!isOpen) {
    handleClose();
  }
};
</script>

<template>
  <DialogRoot :open="show" @update:open="handleOpenChange">
    <DialogPortal>
      <ThemeProvider :mode="theme">
        <DialogOverlay
          v-if="showOverlay"
          class="data-[state=open]:animate-overlayShow fixed inset-0 z-50 bg-themeSecondaryColor/50"
        />
        <DialogContent
          :class="[
            'fixed left-[50%] top-[50%] z-[10000] translate-x-[-50%] translate-y-[-50%]',
            'studio-dialog-reflow m-2 flex max-h-[calc(100dvh-2rem)] w-full flex-col overflow-y-auto overflow-x-hidden rounded-lg border bg-themePrimaryColor shadow-2xl',
            'data-[state=open]:animate-contentShow',
            {
              'h-fit-content max-h-[90vh] w-full max-w-[calc(100vw-2rem)] overflow-y-auto overflow-x-hidden':
                size === '2xlarge',
              'h-full max-w-4xl md:max-h-[500px]': size === 'xlarge',
              'h-full max-w-2xl md:max-h-[500px]': size === 'large',
              'h-full max-w-2xl md:h-fit': size === 'h-fit',
              'max-w-xl': size === 'medium',
              'max-w-md': size === 'small'
            }
          ]"
          @interactOutside="e => $emit('onBlur', e as Event)"
        >
          <div
            :class="{
              'border-b': title || $slots.title
            }"
          >
            <div class="flex items-center justify-between p-3">
              <DialogTitle v-if="title" class="text-md font-bold text-themePrimaryTextColor">
                {{ title }}
              </DialogTitle>
              <slot name="title" />
              <DialogClose
                v-if="showCloseButton"
                :disabled="actionsDisabled"
                class="ml-auto"
                data-testid="studio-dialog-cross-button"
                :aria-label="$t('Close dialog')"
                @click="handleClose"
              >
                <IconX class="ml-auto h-4 w-4 font-bold text-themePrimaryTextColor" />
              </DialogClose>
            </div>
          </div>

          <slot />
          <div v-if="message" class="p-3 text-themePrimaryTextColor">
            {{ message }}
          </div>

          <div
            v-if="showFooter"
            data-testid="dialog-actions"
            class="dialog-actions item-center flex min-w-0 max-w-full flex-wrap items-center justify-end gap-2 border-t p-2"
          >
            <slot name="footer-content" />
            <Button
              v-if="secondaryActionTitle"
              id="dialog-secondary-button"
              :disabled="actionsDisabled || secondaryActionDisabled"
              type="secondary"
              :onClick="() => $emit('secondaryAction')"
            >
              {{ secondaryActionTitle }}
            </Button>
            <Button
              v-if="primaryActionTitle"
              id="dialog-primary-button"
              class="ml-0"
              type="primary"
              :disabled="actionsDisabled || primaryActionDisabled"
              :onClick="() => $emit('primaryAction')"
            >
              {{ primaryActionTitle }}
            </Button>
          </div>
        </DialogContent>
      </ThemeProvider>
    </DialogPortal>
  </DialogRoot>
</template>

<style>
.studio-dialog-reflow > * {
  min-inline-size: 0 !important;
  max-inline-size: 100% !important;
  overflow-wrap: anywhere;
}

.dialog-actions {
  flex-wrap: wrap;
  gap: 0.5rem;
}

@keyframes overlayShow {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes contentShow {
  from {
    opacity: 0;
    transform: translate(-50%, -40%) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
}
</style>
